import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, Megaphone, ChevronDown, Lock } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/chi-siamo", label: "Chi Siamo" },
  { to: "/consulenza-imprese-agricole-sardegna", label: "Consulenza Aziendale" },
  { to: "/servizi", label: "Servizi" },
  { to: "/progetti", label: "Progetti", dropdown: true },
  { to: "/contatti", label: "Contatti" },
  { to: "/area-riservata", label: "Area Riservata", reserved: true },
];

function projectHref(p) {
  if (p.link && p.link.trim()) return p.link.trim();
  return `/progetti/${p.slug}`;
}

function isExternal(href) {
  return /^https?:\/\//i.test(href);
}

function ProgettiDropdown({ projects, active }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const onEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const onLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <div className="relative" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <Link
        to="/progetti"
        data-testid="nav-progetti"
        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
          active ? "bg-[#EAF8F0] text-[#007A3D]" : "text-[#1A1A1A] hover:text-[#007A3D]"
        }`}
      >
        Progetti
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </Link>
      <div
        data-testid="nav-progetti-dropdown"
        className={`absolute left-1/2 -translate-x-1/2 top-full pt-3 z-[60] transition-all duration-200 origin-top ${
          open ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-1 pointer-events-none"
        }`}
      >
        <div className="min-w-[280px] bg-white rounded-2xl shadow-[0_20px_60px_-20px_rgba(0,40,20,0.25)] border border-[#E5E7EB] p-2">
          {projects.length === 0 && (
            <div className="px-4 py-3 text-sm text-[#6B7280]">Nessun progetto pubblicato</div>
          )}
          {projects.map((p) => {
            const href = projectHref(p);
            const ext = isExternal(href);
            const cls =
              "block px-4 py-3 rounded-xl text-sm font-medium text-[#1A1A1A] hover:bg-[#EAF8F0] hover:text-[#007A3D] transition-colors";
            return ext ? (
              <a
                key={p.slug}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`nav-progetti-item-${p.slug}`}
                className={cls}
                onClick={() => setOpen(false)}
              >
                {p.title}
              </a>
            ) : (
              <Link
                key={p.slug}
                to={href}
                data-testid={`nav-progetti-item-${p.slug}`}
                className={cls}
                onClick={() => setOpen(false)}
              >
                {p.title}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MobileProgetti({ projects, onNavigate }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="mobile-nav-progetti-toggle"
        className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium text-[#1A1A1A] hover:bg-[#EAF8F0]"
      >
        <span>Progetti</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="pl-4 py-1 flex flex-col gap-1 border-l-2 border-[#EAF8F0] ml-4 my-1">
            <Link
              to="/progetti"
              onClick={onNavigate}
              data-testid="mobile-nav-progetti-index"
              className="px-3 py-2 rounded-lg text-sm font-medium text-[#007A3D] hover:bg-[#EAF8F0]"
            >
              Tutti i progetti
            </Link>
            {projects.map((p) => {
              const href = projectHref(p);
              const ext = isExternal(href);
              const cls = "px-3 py-2 rounded-lg text-sm text-[#1A1A1A] hover:bg-[#EAF8F0] hover:text-[#007A3D]";
              return ext ? (
                <a
                  key={p.slug}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onNavigate}
                  data-testid={`mobile-nav-progetti-item-${p.slug}`}
                  className={cls}
                >
                  {p.title}
                </a>
              ) : (
                <Link
                  key={p.slug}
                  to={href}
                  onClick={onNavigate}
                  data-testid={`mobile-nav-progetti-item-${p.slug}`}
                  className={cls}
                >
                  {p.title}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [featured, setFeatured] = useState(null);
  const [projects, setProjects] = useState([]);
  const location = useLocation();
  const progettiActive = location.pathname.startsWith("/progetti");

  useEffect(() => {
    api.get("/blog/featured").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
  }, []);

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 w-full">
      {featured && (
        <Link
          to={featured.url || `/progetti/${featured.slug}`}
          data-testid="announcement-bar"
          className="group block bg-[#007A3D] hover:bg-[#00A651] text-white transition-colors"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-center gap-3 text-sm">
            <Megaphone className="w-4 h-4 shrink-0 text-[#A7E2C0]" />
            <span className="font-semibold uppercase tracking-[0.18em] text-[10px] sm:text-xs text-[#A7E2C0] hidden sm:inline">In Evidenza</span>
            <span className="opacity-80 hidden sm:inline">·</span>
            <span className="line-clamp-1 font-medium">{featured.title}</span>
            <ArrowRight className="w-4 h-4 shrink-0 transition-transform group-hover:translate-x-1" />
          </div>
        </Link>
      )}
      <div className="bg-white/90 backdrop-blur-xl border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-24 md:h-28">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <img src="/assets/logo-betelgez.png" alt="Betelgez Srl" className="h-14 sm:h-16 md:h-20 w-auto" />
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item) =>
              item.dropdown ? (
                <ProgettiDropdown key={item.to} projects={projects} active={progettiActive} />
              ) : item.reserved ? (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `ml-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors inline-flex items-center gap-1.5 ${
                      isActive ? "bg-[#EAF8F0] text-[#007A3D] border-[#00A651]" : "text-[#007A3D] border-[#007A3D]/30 hover:bg-[#EAF8F0] hover:border-[#00A651]"
                    }`
                  }
                >
                  <Lock className="w-3.5 h-3.5" /> {item.label}
                </NavLink>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      isActive ? "bg-[#EAF8F0] text-[#007A3D]" : "text-[#1A1A1A] hover:text-[#007A3D]"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>
          <div className="flex items-center gap-2">
            <Link
              to="/contatti"
              data-testid="header-cta-consulenza"
              className="hidden sm:inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#007A3D] text-white rounded-full px-5 py-2.5 text-sm font-medium transition-colors shadow-lg shadow-[#00A651]/20"
            >
              Richiedi Consulenza <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 rounded-full hover:bg-[#EAF8F0]"
              onClick={() => setOpen(!open)}
              aria-label="Menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-[#E5E7EB] bg-white">
            <div className="px-4 py-4 flex flex-col gap-1">
              {NAV.map((item) =>
                item.dropdown ? (
                  <MobileProgetti key={item.to} projects={projects} onNavigate={() => setOpen(false)} />
                ) : (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-xl text-sm font-medium inline-flex items-center gap-2 ${
                        item.reserved
                          ? (isActive ? "bg-[#EAF8F0] text-[#007A3D] border border-[#00A651]" : "text-[#007A3D] border border-[#007A3D]/30")
                          : (isActive ? "bg-[#EAF8F0] text-[#007A3D]" : "text-[#1A1A1A]")
                      }`
                    }
                  >
                    {item.reserved && <Lock className="w-3.5 h-3.5" />} {item.label}
                  </NavLink>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
